import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ClipboardCheck, Clock, AlertTriangle, Flame, UserX, FileClock, ChevronRight, BellRing } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const API = `${window.__BACKEND_URL__}/api`;

const Widget = ({ icon: Icon, iconColor, title, action, onClick, children, testId }) => (
  <div className="ht-card p-4 flex flex-col" data-testid={testId}>
    <button onClick={onClick} className="flex items-center justify-between mb-2 text-left w-full group">
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4" style={{ color: iconColor }} />
        <h3 className="text-sm font-semibold text-white">{title}</h3>
      </div>
      <span className="flex items-center gap-0.5 text-xs font-semibold group-hover:underline" style={{ color: '#1E88E5' }}>
        {action} <ChevronRight className="w-3.5 h-3.5" />
      </span>
    </button>
    {children}
  </div>
);

export default function SupervisorWidgets({ token }) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [readiness, setReadiness] = useState(null);
  const [queueTotal, setQueueTotal] = useState(null);
  const [exceptions, setExceptions] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [pendingAmendments, setPendingAmendments] = useState(null);
  const [nudging, setNudging] = useState(false);

  const loadAll = useCallback(() => {
    const auth = { headers: { Authorization: `Bearer ${token}` } };
    axios.get(`${API}/payroll/readiness`, auth).then(({ data }) => setReadiness(data)).catch(() => {});
    axios.get(`${API}/time/supervisor-queue`, auth).then(({ data }) => setQueueTotal(data.total ?? (data.entries || []).length)).catch(() => setQueueTotal(0));
    axios.get(`${API}/time/exceptions?status=open`, auth).then(({ data }) => setExceptions(data.exceptions || [])).catch(() => setExceptions([]));
    axios.get(`${API}/supervisor/team-alerts`, auth).then(({ data }) => setAlerts(data)).catch(() => setAlerts({ ot_watch: [], missing_yesterday: [] }));
    axios.get(`${API}/time/amendments?status=pending`, auth).then(({ data }) => setPendingAmendments(data.total ?? (data.amendments || []).length)).catch(() => setPendingAmendments(0));
  }, [token]);

  useEffect(() => {
    if (!token) return;
    loadAll();
    const t = setInterval(loadAll, 60000);
    return () => clearInterval(t);
  }, [token, loadAll]);

  const nudgeAll = async (ev) => {
    ev.stopPropagation();
    const missingWorkers = alerts?.missing_yesterday || [];
    if (missingWorkers.length === 0) return;
    setNudging(true);
    try {
      const { data } = await axios.post(`${API}/supervisor/missed-shift-nudge`,
        { user_ids: missingWorkers.map((w) => w.user_id) },
        { headers: { Authorization: `Bearer ${token}` } });
      const already = (data.skipped || []).filter((s) => s.reason === 'already_nudged_today').length;
      toast({
        title: data.nudged > 0 ? `Nudged ${data.nudged} worker${data.nudged === 1 ? '' : 's'}` : 'No new nudges sent',
        description: `${data.nudged > 0 ? `Email${data.sms_sent > 0 ? ` + ${data.sms_sent} SMS` : ''} + in-app reminder sent. ` : ''}${already > 0 ? `${already} already nudged today.` : ''}`,
      });
    } catch (e) {
      toast({ title: 'Could not send nudges', description: e.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally { setNudging(false); }
  };

  const pct = readiness?.readiness_pct ?? 0;
  const pctColor = pct >= 90 ? '#00C896' : pct >= 60 ? '#FFA000' : '#EF4444';
  const high = (exceptions || []).filter((x) => x.severity === 'high').length;
  const medium = (exceptions || []).filter((x) => x.severity === 'medium').length;
  const otWatch = alerts?.ot_watch || [];
  const missing = alerts?.missing_yesterday || [];

  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="supervisor-widgets">
      {/* 1. Team Payroll Readiness */}
      <Widget icon={ClipboardCheck} iconColor={pctColor} title="Team Payroll Readiness"
        action="View Details" onClick={() => navigate('/approvals')} testId="widget-readiness">
        <p className="text-3xl font-bold" style={{ color: pctColor }} data-testid="widget-readiness-pct">{pct}%</p>
        <div className="w-full h-2 rounded-full overflow-hidden mt-2" style={{ background: '#1F2D3F' }}>
          <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(100, pct)}%`, background: pctColor }} />
        </div>
        <p className="text-[11px] mt-1.5" style={{ color: '#64748B' }}>
          {readiness ? `${readiness.ready_entries}/${readiness.total_entries} entries approved this period` : 'Loading…'}
        </p>
      </Widget>

      {/* 2. Pending Approvals */}
      <Widget icon={Clock} iconColor="#60A5FA" title="Pending Approvals"
        action="View All" onClick={() => navigate('/approvals?tab=queue')} testId="widget-pending-approvals">
        <p className="text-3xl font-bold text-white" data-testid="widget-pending-count">{queueTotal ?? '—'}</p>
        <p className="text-[11px] mt-1.5" style={{ color: '#64748B' }}>
          {queueTotal === 0 ? 'All caught up 🎉' : 'timesheets awaiting your approval'}
        </p>
      </Widget>

      {/* 3. Open Exceptions */}
      <Widget icon={AlertTriangle} iconColor="#FBBF24" title="Open Exceptions"
        action="View All" onClick={() => navigate('/approvals?tab=exceptions')} testId="widget-exceptions">
        <div className="flex items-center gap-2" data-testid="widget-exception-badges">
          <span className="px-2.5 py-1 rounded-lg text-sm font-bold" style={{ background: 'rgba(239,68,68,0.15)', color: '#F87171' }}>
            {high} HIGH
          </span>
          <span className="px-2.5 py-1 rounded-lg text-sm font-bold" style={{ background: 'rgba(255,200,0,0.15)', color: '#FACC15' }}>
            {medium} MEDIUM
          </span>
        </div>
        <p className="text-[11px] mt-2" style={{ color: '#64748B' }}>
          {exceptions === null ? 'Loading…' : `${exceptions.length} open exception${exceptions.length === 1 ? '' : 's'} total`}
        </p>
      </Widget>

      {/* 4. Overtime Alerts */}
      <Widget icon={Flame} iconColor="#FB923C" title="Overtime Alerts"
        action="View Queue" onClick={() => navigate('/approvals?tab=queue')} testId="widget-ot-alerts">
        {alerts === null ? (
          <p className="text-xs" style={{ color: '#64748B' }}>Loading…</p>
        ) : otWatch.length === 0 ? (
          <p className="text-xs py-2" style={{ color: '#64748B' }} data-testid="widget-ot-empty">
            No one is above 75% of the {alerts.weekly_threshold || 44}h OT threshold this week.
          </p>
        ) : (
          <div className="space-y-1.5 max-h-32 overflow-y-auto">
            {otWatch.map((w) => (
              <div key={w.user_id} className="flex items-center justify-between gap-2 rounded-lg px-2.5 py-1.5"
                style={{ background: '#0F1923' }} data-testid={`ot-watch-${w.user_id}`}>
                <span className="text-xs text-white truncate">{w.name}</span>
                <span className="text-xs font-bold whitespace-nowrap"
                  style={{ color: w.pct >= 100 ? '#EF4444' : '#FBBF24' }}>
                  {w.hours}h · {w.pct}%
                </span>
              </div>
            ))}
          </div>
        )}
      </Widget>

      {/* 5. Missing Time */}
      <Widget icon={UserX} iconColor="#A0AEC0" title="Missing Time (Yesterday)"
        action="Corrections" onClick={() => navigate('/approvals?tab=amendments')} testId="widget-missing-time">
        {alerts === null ? (
          <p className="text-xs" style={{ color: '#64748B' }}>Loading…</p>
        ) : missing.length === 0 ? (
          <p className="text-xs py-2" style={{ color: '#64748B' }} data-testid="widget-missing-empty">
            Everyone logged a completed shift yesterday.
          </p>
        ) : (
          <>
            <p className="text-3xl font-bold text-white" data-testid="widget-missing-count">{missing.length}</p>
            <p className="text-[11px] mb-1.5" style={{ color: '#64748B' }}>worker{missing.length === 1 ? '' : 's'} with no completed shift yesterday</p>
            <div className="flex flex-wrap gap-1 max-h-20 overflow-y-auto">
              {missing.slice(0, 8).map((w) => (
                <span key={w.user_id} className="text-[10px] px-1.5 py-0.5 rounded-full"
                  style={{ background: '#1F2D3F', color: '#A0AEC0' }}>{w.name}</span>
              ))}
              {missing.length > 8 && (
                <span className="text-[10px] px-1.5 py-0.5" style={{ color: '#64748B' }}>+{missing.length - 8} more</span>
              )}
            </div>
            <button onClick={nudgeAll} disabled={nudging}
              className="mt-2.5 w-full flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold disabled:opacity-50"
              style={{ background: 'rgba(30,136,229,0.15)', color: '#60A5FA', border: '1px solid rgba(30,136,229,0.3)' }}
              data-testid="nudge-all-btn">
              <BellRing className="w-3.5 h-3.5" />
              {nudging ? 'Sending…' : `Nudge ${missing.length === 1 ? 'Worker' : 'All'} (SMS + email + bell)`}
            </button>
          </>
        )}
      </Widget>

      {/* 6. Pending Corrections */}
      <Widget icon={FileClock} iconColor="#FACC15" title="Pending Corrections"
        action="Review" onClick={() => navigate('/approvals?tab=amendments')} testId="widget-pending-corrections">
        <p className="text-3xl font-bold text-white" data-testid="widget-corrections-count">{pendingAmendments ?? '—'}</p>
        <p className="text-[11px] mt-1.5" style={{ color: '#64748B' }}>
          {pendingAmendments === 0 ? 'No missing-time requests waiting' : 'missing-time requests awaiting review'}
        </p>
      </Widget>
    </div>
  );
}
